import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';

interface AdminUser {
  sub: string;
  email: string;
  name: string;
  roles: string[];
}

interface AuthState {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: () => void;
  logout: () => Promise<void>;
  clearError: () => void;
  checkAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

const API_BASE = '/api/v1/admin/auth';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  // Check if user is authenticated by calling /me endpoint
  const checkAuth = useCallback(async (): Promise<boolean> => {
    try {
      const res = await fetch(`${API_BASE}/me`, {
        credentials: 'include', // Important: include cookies
      });

      if (res.ok) {
        const data = await res.json();
        const user: AdminUser = {
          sub: data.sub,
          email: data.email || '',
          name: data.name || data.email || 'Admin',
          roles: data.roles || [],
        };
        setState(prev => ({
          ...prev,
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        }));
        return true;
      } else {
        setState(prev => ({
          ...prev,
          user: null,
          isAuthenticated: false,
          isLoading: false,
        }));
        return false;
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        isLoading: false,
      }));
      return false;
    }
  }, []);

  // Check auth status on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Login - redirect to backend which will redirect to Cognito
  const login = useCallback(() => {
    // Redirect to backend login endpoint
    // Backend will redirect to Cognito Hosted UI
    window.location.href = `${API_BASE}/login`;
  }, []);

  // Logout - call backend to clear session
  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE}/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout error:', err);
    }

    setState(prev => ({
      ...prev,
      user: null,
      isAuthenticated: false,
      error: null,
    }));
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    clearError,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
